import { FormEvent, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { fetchJSON, getWpBaseUrl } from "../../utils/api";
import type { WPPage } from "../../content/contentClient";
import type { LanguageCode } from "../../hooks/useLanguage";

type NewsletterSignupProps = {
  lang: LanguageCode;
  page?: WPPage | null;
};

type NewsletterResponse = {
  message?: string;
};

type FormState = {
  email: string;
  consent: boolean;
  company: string;
  interests: string[];
};

const DEFAULT_STATE: FormState = {
  email: "",
  consent: false,
  company: "",
  interests: []
};

const LIMITS = {
  email: 190
} as const;

const NEWSLETTER_PAGE_SLUGS = new Set(["newsletter", "newsletters"]);

const TEXT = {
  de: {
    title: "Newsletter",
    intro: "Melde dich fuer Neuigkeiten, Einblicke und neue Texte an.",
    email: "E-Mail",
    consent: "Ich moechte den Newsletter erhalten und stimme der Verarbeitung meiner Daten zu.",
    privacyHint: "Die Anmeldung erfolgt per Double-Opt-In. Du kannst dich jederzeit wieder abmelden.",
    submit: "Anmelden",
    success: "Bitte bestaetige deine Anmeldung ueber den Link in der E-Mail.",
    error: "Die Anmeldung konnte gerade nicht abgeschlossen werden. Bitte spaeter erneut versuchen.",
    required: "Bitte gib deine E-Mail-Adresse ein und bestaetige die Einwilligung.",
    invalidEmail: "Bitte gib eine gueltige E-Mail-Adresse ein.",
    interests: "Welche Themen interessieren dich ausserdem?"
  },
  en: {
    title: "Newsletter",
    intro: "Subscribe for updates, insights and new writing.",
    email: "Email",
    consent: "I would like to receive the newsletter and agree to the processing of my data.",
    privacyHint: "The signup uses double opt-in. You can unsubscribe at any time.",
    submit: "Subscribe",
    success: "Please confirm your subscription via the link in the email.",
    error: "The signup could not be completed right now. Please try again later.",
    required: "Please enter your email address and confirm your consent.",
    invalidEmail: "Please enter a valid email address.",
    interests: "Which topics are you also interested in?"
  }
} as const;

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const parseInterestOptions = (value?: string) =>
  (value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

export const isNewsletterPage = (page?: WPPage | null) => {
  const slug = page?.link ? new URL(page.link).pathname.split("/").filter(Boolean).pop() : "";
  const pageSlug = slug || "";
  if (NEWSLETTER_PAGE_SLUGS.has(pageSlug)) return true;
  return page?.acf?.newsletter_enabled === true && Boolean(page?.acf?.newsletter_group_id);
};

export default function NewsletterSignup({ lang, page }: NewsletterSignupProps) {
  const [form, setForm] = useState<FormState>(DEFAULT_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [startedAt] = useState(() => Date.now());

  const text = TEXT[lang];
  const acf = page?.acf || {};
  const title = acf.newsletter_headline || text.title;
  const intro = acf.newsletter_intro || text.intro;
  const submitLabel = acf.newsletter_button_label || text.submit;
  const successMessage = acf.newsletter_success_message || text.success;
  const privacyHint = acf.newsletter_privacy_hint || text.privacyHint;
  const interestLabel = acf.newsletter_interest_label || text.interests;
  const interestHint = acf.newsletter_interest_hint || "";
  const interestOptions = parseInterestOptions(acf.newsletter_interest_options);
  const defaultTag = (acf.newsletter_default_tag || "").trim();

  const toggleInterest = (interest: string, checked: boolean) => {
    setForm((current) => ({
      ...current,
      interests: checked
        ? Array.from(new Set([...current.interests, interest]))
        : current.interests.filter((item) => item !== interest)
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.email.trim() || !form.consent) {
      setError(text.required);
      return;
    }

    if (!isValidEmail(form.email)) {
      setError(text.invalidEmail);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        email: form.email.trim(),
        consent: form.consent,
        company: form.company,
        startedAt,
        pageSlug: page?.link ? new URL(page.link).pathname.split("/").filter(Boolean).pop() : "",
        pageTitle: page?.title?.rendered || "",
        pageLanguage: lang,
        formId: acf.newsletter_form_id || "",
        defaultTag,
        interests: form.interests
      };

      const response = await fetchJSON<NewsletterResponse>("/multisite/v1/newsletter/subscribe", {
        baseUrl: getWpBaseUrl(),
        method: "POST",
        body: payload
      });

      setSuccess(response?.message || successMessage);
      setForm(DEFAULT_STATE);
    } catch {
      setError(text.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        mt: 5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        p: { xs: 2.5, md: 3.5 }
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {intro}
          </Typography>
        </Box>

        {success ? <Alert severity="success">{success}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label={text.email}
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              required
              inputProps={{ maxLength: LIMITS.email }}
              fullWidth
            />
            <Box sx={{ position: "absolute", left: -10000, top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <TextField
                tabIndex={-1}
                autoComplete="new-password"
                label="Company"
                value={form.company}
                onChange={(event) => setForm((current) => ({ ...current, company: event.target.value }))}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.consent}
                  onChange={(event) => setForm((current) => ({ ...current, consent: event.target.checked }))}
                />
              }
              label={text.consent}
            />
            {interestOptions.length ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  {interestLabel}
                </Typography>
                <Stack spacing={0.5}>
                  {interestOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={form.interests.includes(option)}
                          onChange={(event) => toggleInterest(option, event.target.checked)}
                        />
                      }
                      label={option}
                    />
                  ))}
                </Stack>
                {interestHint ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {interestHint}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {privacyHint}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitLabel}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
